import { useState, useEffect, useCallback } from 'react';
import { EventMediaUpload } from './EventMediaUpload';
import { EventBasicInfoFields } from './EventForm/EventBasicInfoFields';
import { EventScheduleFields } from './EventForm/EventScheduleFields';
import { EventCapacityPricingFields } from './EventForm/EventCapacityPricingFields';
import { EventLocationFields } from './EventForm/EventLocationFields';
import { EventContactFields } from './EventForm/EventContactFields';
import { FormSection } from './ui/FormSection';
import type { EventFormData, EventType, EventStatus, EventAudience } from '@wineryxplorer/shared';
import type { EventFormErrors } from './EventForm/types';

export interface EventFormProps {
  initialData?: Partial<EventFormData>;
  onSubmit: (data: EventFormData) => Promise<void>;
  onCancel: () => void;
  venueId?: string;
  isSubmitting?: boolean;
  hideButtons?: boolean;
  submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

export const EventForm = ({
  initialData,
  onSubmit,
  onCancel,
  venueId: propVenueId,
  isSubmitting = false,
  hideButtons = false,
  submitRef,
}: EventFormProps) => {
  // Form state
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [eventType, setEventType] = useState<EventType>(initialData?.eventType || 'tasting');
  const [status, setStatus] = useState<EventStatus>(initialData?.status || 'draft');
  const [startDate, setStartDate] = useState(
    initialData?.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : ''
  );
  const [startTime, setStartTime] = useState<{ hours: number; minutes: number } | undefined>(
    initialData?.startTime
  );
  const [duration, setDuration] = useState<number | undefined>(initialData?.duration);
  const [venueId, setVenueId] = useState(initialData?.venueId || propVenueId || '');
  const [maxAttendees, setMaxAttendees] = useState(initialData?.maxAttendees?.toString() || '');
  const [priceAmount, setPriceAmount] = useState(initialData?.price?.amount.toString() || '');
  const [requiresReservation, setRequiresReservation] = useState(
    initialData?.requiresReservation ?? true
  );
  const [audience, setAudience] = useState<EventAudience>(initialData?.audience ?? 'public');
  const [tags, setTags] = useState(initialData?.tags?.join(', ') || '');
  const [contactEmail, setContactEmail] = useState(initialData?.contactInfo?.email || '');
  const [contactPhone, setContactPhone] = useState(initialData?.contactInfo?.phone || '');
  const [photoIds, setPhotoIds] = useState<string[]>(initialData?.photoIds || []);
  const [videoIds, setVideoIds] = useState<string[]>(initialData?.videoIds || []);
  
  // Location state
  const [locationName, setLocationName] = useState(initialData?.location?.name || '');
  const [locationStreet, setLocationStreet] = useState(initialData?.location?.address?.street || '');
  const [locationCity, setLocationCity] = useState(initialData?.location?.address?.city || '');
  const [locationState, setLocationState] = useState(initialData?.location?.address?.state || '');
  const [locationZipCode, setLocationZipCode] = useState(initialData?.location?.address?.zipCode || '');
  const [locationCountry, setLocationCountry] = useState(initialData?.location?.address?.country || 'USA');

  // Validation errors
  const [errors, setErrors] = useState<EventFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Update venueId when prop changes
  useEffect(() => {
    if (propVenueId && !venueId) {
      setVenueId(propVenueId);
    }
  }, [propVenueId, venueId]);

  const validateForm = useCallback((): boolean => {
    const newErrors: EventFormErrors = {};

    // Required fields
    if (!name.trim()) {
      newErrors.name = 'Event name is required';
    } else if (name.length > 200) {
      newErrors.name = 'Event name must be 200 characters or less';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const selectedDate = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.startDate = 'Start date must be in the future';
      }
    }

    // Validate start time if provided
    if (startTime) {
      if (startTime.hours < 0 || startTime.hours > 23) {
        newErrors.startTime = 'Hours must be between 0 and 23';
      }
      if (startTime.minutes < 0 || startTime.minutes > 59) {
        newErrors.startTime = 'Minutes must be between 0 and 59';
      }
    }

    // Validate duration if provided
    if (duration !== undefined && duration <= 0) {
      newErrors.duration = 'Duration must be a positive number';
    }

    // Validate max attendees if provided
    if (maxAttendees) {
      const maxAttendeesNum = parseInt(maxAttendees, 10);
      if (isNaN(maxAttendeesNum) || maxAttendeesNum < 1) {
        newErrors.maxAttendees = 'Max attendees must be at least 1';
      }
    }

    // Validate price if provided
    if (priceAmount) {
      const priceNum = parseFloat(priceAmount);
      if (isNaN(priceNum) || priceNum < 0) {
        newErrors.price = 'Price must be a non-negative number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, startDate, startTime, duration, maxAttendees, priceAmount]);

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = useCallback(async () => {
    // Mark all fields as touched
    setTouched({
      name: true,
      eventType: true,
      startDate: true,
      startTime: true,
      duration: true,
      maxAttendees: true,
      price: true,
    });

    if (!validateForm()) {
      return;
    }

    const formData: EventFormData = {
      name: name.trim(),
      description: description.trim() || undefined,
      eventType,
      status,
      startDate: startDate, // Send as ISO string, Zod will coerce to Date
      startTime,
      duration,
      venueId: venueId || undefined,
      maxAttendees: maxAttendees ? parseInt(maxAttendees, 10) : undefined,
      price: priceAmount
        ? { amount: parseFloat(priceAmount), currency: 'USD' }
        : undefined,
      requiresReservation,
      audience,
      tags: tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
      contactInfo:
        contactEmail || contactPhone
          ? {
              email: contactEmail || undefined,
              phone: contactPhone || undefined,
            }
          : undefined,
      location:
        locationStreet || locationCity || locationState || locationZipCode || locationName
          ? {
              name: locationName || undefined,
              address: {
                street: locationStreet || undefined,
                city: locationCity || undefined,
                state: locationState || undefined,
                zipCode: locationZipCode || undefined,
                country: locationCountry || undefined,
              },
            }
          : undefined,
      heroImageId: undefined,
      imageIds: [],
      photoIds,
      videoIds,
    };

    await onSubmit(formData);
  }, [
    name,
    description,
    eventType,
    status,
    startDate,
    startTime,
    duration,
    venueId,
    maxAttendees,
    priceAmount,
    requiresReservation,
    audience,
    tags,
    contactEmail,
    contactPhone,
    locationName,
    locationStreet,
    locationCity,
    locationState,
    locationZipCode,
    locationCountry,
    photoIds,
    videoIds,
    onSubmit,
    validateForm,
  ]);

  // Expose submit handler to parent via ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
  }, [submitRef, handleSubmit]);

  return (
    <div>
      <EventBasicInfoFields
        name={name}
        onNameChange={setName}
        eventType={eventType}
        onEventTypeChange={setEventType}
        status={status}
        onStatusChange={setStatus}
        description={description}
        onDescriptionChange={setDescription}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <EventScheduleFields
        startDate={startDate}
        onStartDateChange={setStartDate}
        startTime={startTime}
        onStartTimeChange={setStartTime}
        duration={duration}
        onDurationChange={setDuration}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <EventCapacityPricingFields
        maxAttendees={maxAttendees}
        onMaxAttendeesChange={setMaxAttendees}
        priceAmount={priceAmount}
        onPriceAmountChange={setPriceAmount}
        requiresReservation={requiresReservation}
        onRequiresReservationChange={setRequiresReservation}
        audience={audience}
        onAudienceChange={setAudience}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <EventLocationFields
        locationName={locationName}
        onLocationNameChange={setLocationName}
        locationStreet={locationStreet}
        onLocationStreetChange={setLocationStreet}
        locationCity={locationCity}
        onLocationCityChange={setLocationCity}
        locationState={locationState}
        onLocationStateChange={setLocationState}
        locationZipCode={locationZipCode}
        onLocationZipCodeChange={setLocationZipCode}
        locationCountry={locationCountry}
        onLocationCountryChange={setLocationCountry}
        isSubmitting={isSubmitting}
      />

      <EventContactFields
        contactEmail={contactEmail}
        onContactEmailChange={setContactEmail}
        contactPhone={contactPhone}
        onContactPhoneChange={setContactPhone}
        isSubmitting={isSubmitting}
      />

      {/* Tags */}
      <FormSection title="Tags" description="Separate tags with commas.">
        <label htmlFor="tags" className="sr-only">
          Tags
        </label>
        <input
          id="tags"
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          disabled={isSubmitting}
          placeholder="wine, tasting, outdoor (comma-separated)"
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
            isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        />
      </FormSection>

      {/* Media Upload */}
      {venueId && (
        <EventMediaUpload
          venueId={venueId}
          photoIds={photoIds}
          videoIds={videoIds}
          onPhotosChange={setPhotoIds}
          onVideosChange={setVideoIds}
        />
      )}

      {/* Form Actions */}
      {!hideButtons && (
        <div className="mt-6 flex gap-4 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex-1 px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save Event'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={`px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
